"use client";

import { useEffect, useRef, useState, type CSSProperties } from "react";

/**
 * Easter egg: typing `gorigori` "blind" — with no input focused — sets the edges of the window on
 * fire for a few seconds. Mounted once in `Providers`, so it works on every page.
 *
 * Purely decorative: the overlay ignores the pointer and is hidden from assistive tech, and typing
 * in a field never triggers it, so a vehicle search for "gorigori" stays a search.
 */

const TRIGGER = "gorigori";
/** Matches `flame-life` in `app/globals.css`. */
const BURN_MS = 6000;

/** Tongues per edge; adjacent ones overlap so the strip reads as one fire. */
const TONGUES = 64;
const EMBERS = 8;

/** Deterministic 0..1 noise, so each tongue keeps its own size and rhythm across renders. */
function noise(index: number, seed: number) {
  const x = Math.sin(index * 12.9898 + seed * 78.233) * 43758.5453;
  return x - Math.floor(x);
}

function isTypingTarget(element: Element | null): boolean {
  if (!(element instanceof HTMLElement)) return false;
  if (element.isContentEditable) return true;
  const tag = element.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}

function FlameStrip({ edge, seed }: { edge: "top" | "right" | "bottom" | "left"; seed: number }) {
  return (
    <div className={`flame-strip flame-strip-${edge}`}>
      {Array.from({ length: TONGUES }, (_, index) => {
        const width = 100 / TONGUES;
        const style = {
          left: `${(index - 0.6) * width}%`,
          width: `${width * 2.2}%`,
          height: `${45 + noise(index, seed) * 55}%`,
          "--flame-duration": `${0.45 + noise(index, seed + 1) * 0.5}s`,
          "--flame-delay": `${-noise(index, seed + 2)}s`,
        } as CSSProperties;
        return <span key={index} className="flame-tongue" style={style} />;
      })}
    </div>
  );
}

function Flames() {
  return (
    <div className="flame-frame" aria-hidden="true">
      <div className="flame-frame-glow" />
      <FlameStrip edge="bottom" seed={1} />
      <FlameStrip edge="top" seed={2} />
      <FlameStrip edge="left" seed={3} />
      <FlameStrip edge="right" seed={4} />
      {Array.from({ length: EMBERS }, (_, index) => {
        const style = {
          left: `${noise(index, 9) * 100}%`,
          "--flame-duration": `${1.6 + noise(index, 10) * 1.6}s`,
          "--flame-delay": `${-noise(index, 11) * 3}s`,
          "--ember-drift": `${(noise(index, 12) - 0.5) * 30}px`,
        } as CSSProperties;
        return <span key={index} className="flame-ember" style={style} />;
      })}
    </div>
  );
}

export function FlameFrame() {
  /** Bumped on every trigger, so a repeat while burning remounts the overlay and restarts it. */
  const [burnKey, setBurnKey] = useState(0);
  const [burning, setBurning] = useState(false);
  const bufferRef = useRef("");

  useEffect(() => {
    let timer: number | null = null;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.repeat || event.ctrlKey || event.metaKey || event.altKey) return;
      if (event.key.length !== 1) return;
      if (isTypingTarget(event.target as Element | null) || isTypingTarget(document.activeElement)) {
        bufferRef.current = "";
        return;
      }

      const buffer = (bufferRef.current + event.key.toLowerCase()).slice(-TRIGGER.length);
      bufferRef.current = buffer;
      if (buffer !== TRIGGER) return;

      bufferRef.current = "";
      setBurnKey((key) => key + 1);
      setBurning(true);
      if (timer !== null) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        timer = null;
        setBurning(false);
      }, BURN_MS);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      if (timer !== null) window.clearTimeout(timer);
    };
  }, []);

  return burning ? <Flames key={burnKey} /> : null;
}
